/* eslint-disable jsx-a11y/label-has-associated-control */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from "react";
import FullCalendar from "@fullcalendar/react"; // must go before plugins
import dayGridPlugin from "@fullcalendar/daygrid"; // a plugin!
import timeGridPlugin from "@fullcalendar/timegrid"; // a plugin!
import interactionPlugin from "@fullcalendar/interaction";
import { Modal } from "react-bootstrap";
import { v4 as uuidv4 } from "uuid";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import CalendarDetails from "./CalendarDetails";
import NewEventForm from "./NewEventForm";
import EventEditForm from "./EventEditForm";
import ParticipantList from "./ParticipantList";
import JsonCollapse from "../JsonCollapse";
import {
  addNewCalendar,
  selectCalendar,
  setCalendar,
} from "../../../store/slices/projectSlice";

export default function CalendarWidget() {
  const navigate = useNavigate();
  const params = useParams();
  // const [currentCalendar, setCurrentCalendar] = useState(null);
  const [currentCalendarAPI, setCurrentCalendarAPI] = useState(null);
  const [newEvent, setNewEvent] = useState(null);
  const [event, setEvent] = useState(null);
  const [showParticipants, setShowParticipants] = useState(null);

  const currentCalendar = useSelector(selectCalendar(params.calendarUUID));

  const dispatch = useDispatch();
  const setCurrentCalendar = (fn) => dispatch(setCalendar(fn(currentCalendar)));

  const handleClose = () => {
    if (currentCalendar?.Label) {
      navigate(`/library/logic/builder/${params.projectID}`);
    } else {
      toast.error("Label is missing!");
    }
  };

  const handleCalendarEdit = (e) => {
    setCurrentCalendar((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  // show new event form when calendar is clicked
  const handleDateSelect = (selectInfo) => {
    setEvent(null); // hide event edit form
    setCurrentCalendarAPI(selectInfo.view.calendar);
    setNewEvent({
      id: uuidv4(),
      title: `New event - ${selectInfo.start.toDateString()}`,
      start: selectInfo.startStr,
      end: selectInfo.endStr,
      allDay: selectInfo.allDay,
      extendedProps: {
        capacity: "",
        description: "",
      },
    });
  };

  // add new event to the calendar
  const handleSaveEvent = () => {
    currentCalendarAPI.unselect(); // clear date selection
    if (newEvent !== null) {
      currentCalendarAPI.addEvent(newEvent);
      setNewEvent(null);
    }
  };

  // show event details when event is clicked
  const handleEventClick = (e) => {
    setEvent(e.event);
    setNewEvent(null); // hide new event form
  };

  const handleEvents = (events) => {
    setCurrentCalendar((prev) => ({
      ...prev,
      CalendarEvents: events.map((e) => e.toPlainObject()),
    }));
  };

  const renderEventContent = (eventInfo) => (
    <>
      <b>{eventInfo.timeText}</b> <i>{eventInfo.event.title}</i>
      <div>{eventInfo.event.extendedProps.description}</div>
    </>
  );

  const renderSidebar = () => (
    <div className="h-100 w-25 overflow-auto p-3">
      {
        // calendar details
        currentCalendar && (
          <CalendarDetails
            currentCalendar={currentCalendar}
            handleCalendarEdit={handleCalendarEdit}
          />
        )
      }

      {
        // new event form
        newEvent && (
          <NewEventForm
            newEvent={newEvent}
            setNewEvent={setNewEvent}
            handleSaveEvent={handleSaveEvent}
            calendarAPI={currentCalendarAPI}
          />
        )
      }

      {
        // Edit event form
        event && (
          <EventEditForm
            event={event}
            calendar={currentCalendar}
            setEvent={setEvent}
            showParticipants={() => setShowParticipants(event.id)}
          />
        )
      }
    </div>
  );

  useEffect(() => {
    if (params.calendarUUID) {
      if (params.calendarUUID === "create_new") {
        const newCalUUID = uuidv4();
        dispatch(addNewCalendar(newCalUUID));
        navigate(`/library/logic/builder/${params.projectID}/calendar/${newCalUUID}`, {
          replace: true,
        });
      }
    }
  }, [params.calendarUUID]);

  return (
    <Modal
      size="xl"
      centered
      show={params.calendarUUID && currentCalendar}
      onHide={handleClose}
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="icon fa fa-edit" />
          {params.calendarUUID === "create_new" ? "Create " : "Edit "}
          calendar
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        onClick={(e) => {
          if (!e.target.closest(".new-event-form")) {
            setNewEvent(null);
          }
        }}
      >
        {showParticipants !== null && (
          <ParticipantList
            eventId={showParticipants}
            back={() => setShowParticipants(null)}
          />
        )}
        {currentCalendar !== null && (
          <div className="d-flex" style={{ height: "calc(100vh - 200px)" }}>
            {renderSidebar()}
            <div className="flex-grow-1 py-3 px-5">
              <FullCalendar
                height="100%"
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                headerToolbar={{
                  left: "prev,next today",
                  center: "title",
                  right: "dayGridMonth,timeGridWeek,timeGridDay",
                }}
                initialView="dayGridMonth"
                firstDay={1}
                navLinks
                editable
                selectable
                selectMirror
                unselectCancel=".new-event-form"
                dayMaxEvents
                droppable
                initialEvents={currentCalendar?.CalendarEvents} // alternatively, use the `events` setting to fetch from a feed
                select={handleDateSelect}
                eventContent={renderEventContent} // custom render function
                eventClick={handleEventClick}
                eventsSet={handleEvents} // called after events are initialized/added/changed/removed
                // eventChange={({event}) => {setEvent(event)}}
                /* you can update a remote database when these fire:
                  eventAdd={function(){}}
                  eventRemove={function(){}}
            */
              />
            </div>
          </div>
        )}
        <JsonCollapse json={currentCalendar} />
      </Modal.Body>
    </Modal>
  );
}
